import React, { useMemo, useRef } from "react";
import { TimelineEvent } from "../../utils/interfaces";
import VirtualizedList from "../VirtualizedList";

// Define the interface for the component props
interface ActionPickerForAccordionProps {
  events: TimelineEvent[];
  onHoverEvent: (time: number) => void;
  onClickEvent: (recordingId: string, time: number) => void;
}

const ActionPickerForAccordion: React.FC<ActionPickerForAccordionProps> = ({
  events,
  onHoverEvent,
  onClickEvent,
}) => {
  const parentRef = useRef<HTMLDivElement>(null);
  // Sort events by time, smallest to largest
  const sortedEvents = useMemo(
    () => [...events].sort((a, b) => a.time - b.time),
    [events],
  );

  return (
    <div
      ref={parentRef}
      className='action-picker-accordion overflow-auto h-full text-xs leading-5 font-medium text-blue-500'
    >
      <VirtualizedList
        items={sortedEvents}
        parentRef={parentRef}
        textSelector='#payload'
        estimateSize={() => 28}
        getCopyText={(leftIndex, leftOffset, rightIndex, rightOffset) => {
          const copyItems = sortedEvents
            .slice(leftIndex, rightIndex + 1)
            .map(({ text }) => text);

          const { length } = copyItems;
          if (length === 1) {
            copyItems[0] = copyItems[0].slice(leftOffset, rightOffset);
          } else if (length > 1) {
            copyItems[0] = copyItems[0].slice(leftOffset, copyItems[0].length);
            copyItems[length - 1] = copyItems[length - 1].slice(0, rightOffset);
          }
          return copyItems.join("\n");
        }}
        renderItem={(event: TimelineEvent) => (
          <div
            className={
              event.type === "pageNavigation"
                ? "flex items-center"
                : "ml-2 border-l-2 border-green-500"
            }
          >
            <div
              className='action-item flex items-center cursor-pointer py-1 px-2 hover:bg-gray-200 max-w-full'
              onMouseEnter={() => onHoverEvent(event.time)}
              onClick={(e) => {
                e.stopPropagation();
                onClickEvent(event.recordingId, event.time);
              }}
              role='button'
              tabIndex={0}
            >
              <div className='truncate'>{event.text}</div>
            </div>
          </div>
        )}
      />
    </div>
  );
};

export default ActionPickerForAccordion;
